import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useWorldStore } from '../state/worldStore';
import { sendSay, tableMate } from '../services/worldSocket';
import { venueAgentTake } from '../city/venueAgent';
import { useStackSlot, setHudVar } from './hudStack';

// PlayerBar — the one bar at the bottom: what is playing, and what you say.
//
// Khari, 2026-10-23, on a phone: *"please help me cleanup the HUD… We may need
// to add a global player so we can clean up the radio, music, track title, etc.
// Right now, it is just floating. The same player may be able to double as the
// chat input."* Before this, the broadcast card, the Music toggle and the 💬
// proximity chat were three separate pieces placed by three separate guesses.
//
//   PLAYER  what is on (status · artist — title), ♥ to like it (members),
//           ⏯ the station, 💬 to talk
//   CHAT    the same bar becomes the input; recent lines float just above it
//           (what you say appears over your figure to people nearby)
//
// It owns the bottom edge: on a phone it runs edge to edge (the joystick sits
// above it), on a desktop it sits bottom-right. Its height is published as the
// CSS variable --hud-player-h so the joystick and the chat docks stand on top
// of it rather than each guessing an offset (BottomSlot's lesson).
//
// The station's own life (starts with the fly-in, the pulse, the modulation
// track) stays in Hud; this is only its face.

const ACCENT = '#ffb25e';
const BAR_H = 56;
const GAP = 10;
const CENTRE_HALF = 180;   // the bottom-centre stack's half-width (BottomSlot: Listen together, a session invite)

export default function PlayerBar({
  compact, ticker, statusText, musicOn, onToggleMusic, art = null,
  likeable = false, isLiked = false, likeBusy = false, onLike = null,
}) {
  const chat = useWorldStore((s) => s.chat);
  // AT A TABLE WITH SOMEBODY (2026-10-31): what you say goes to them alone (the
  // server routes it: API sharedSeatStore.tableMates), and the bar says so.
  useWorldStore((s) => s.roomSeats);   // re-read when the room's chairs change
  const mate = tableMate();
  const [mode, setMode] = useState('player');
  const [text, setText] = useState('');
  const inputRef = useRef(null);
  const linesSlot = useStackSlot();

  useEffect(() => { if (mode === 'chat') inputRef.current?.focus(); }, [mode]);
  // "Talk to them" from elsewhere in the world (2026-10-30: a venue host who came
  // down from the booth to you, VenueStage — what you say here reaches them, API
  // sockets/venueMingle): the input opens, saying who you are talking to.
  const [placeholder, setPlaceholder] = useState(null);
  useEffect(() => {
    const open = (e) => { setPlaceholder(e?.detail?.placeholder || null); setMode('chat'); };
    window.addEventListener('ht:chat-open', open);
    return () => window.removeEventListener('ht:chat-open', open);
  }, []);
  useEffect(() => { if (mode === 'player') setPlaceholder(null); }, [mode]);
  // Publish the bar's height for whatever stands on top of it.
  useEffect(() => {
    const root = document.documentElement;
    root.style.setProperty('--hud-player-h', `${BAR_H + GAP}px`);
    return () => root.style.removeProperty('--hud-player-h');
  }, []);
  // …and how much of the bottom CENTRE it takes (2026-11-01, Khari: "the 'listen
  // together' button overlays the player"): edge to edge on a phone or a narrow
  // window, and a desktop's right-hand bar reaches the middle of a mid-width one.
  // Measured off the bar itself, so it is true at any width; BottomSlot stands on it.
  const barEl = useRef(null);
  const measureBar = useCallback(() => {
    const el = barEl.current;
    const r = el?.getBoundingClientRect();
    if (!r || !r.width || !r.height) { setHudVar('--hud-centre-clear', null); return; }   // gone, or hidden
    const mid = window.innerWidth / 2;
    const under = r.left < mid + CENTRE_HALF && r.right > mid - CENTRE_HALF;
    setHudVar('--hud-centre-clear', under ? `${Math.ceil(window.innerHeight - r.top + 8)}px` : null);
  }, []);
  const barRef = useCallback((el) => { barEl.current = el; measureBar(); }, [measureBar]);
  useLayoutEffect(() => { measureBar(); });   // compact ↔ wide, player ↔ chat
  useEffect(() => {
    window.addEventListener('resize', measureBar);
    return () => { window.removeEventListener('resize', measureBar); setHudVar('--hud-centre-clear', null); };
  }, [measureBar]);

  // Alone with a venue's host, their voice agent hears it first (city/venueAgent).
  const send = () => { const t = text.trim(); if (t) { sendSay(t, { viaAgent: venueAgentTake(t) }); setText(''); } };

  const place = compact
    ? { left: 'max(12px, env(safe-area-inset-left))', right: 'max(12px, env(safe-area-inset-right))', bottom: 'max(10px, env(safe-area-inset-bottom))' }
    : { right: 16, bottom: 16, width: 'min(420px, 42vw)' };
  const bar = {
    position: 'fixed', ...place, zIndex: 46, height: BAR_H, boxSizing: 'border-box',
    display: 'flex', alignItems: 'center', gap: 8, padding: '0 8px', borderRadius: 16,
    background: 'rgba(16,10,6,0.78)', border: '1px solid rgba(255,178,94,0.35)',
    backdropFilter: 'blur(10px)', WebkitBackdropFilter: 'blur(10px)', boxShadow: '0 8px 28px rgba(0,0,0,0.35)',
    color: '#ffe9c4', fontFamily: 'system-ui, sans-serif', pointerEvents: 'auto',
  };
  const icon = {
    flex: 'none', width: 40, height: 40, borderRadius: 12, border: 'none', cursor: 'pointer',
    background: 'rgba(255,178,94,0.12)', color: '#ffd27d', fontSize: 17, lineHeight: 1,
    display: 'grid', placeItems: 'center', padding: 0,
  };

  if (mode === 'chat') {
    const recent = chat.slice(-4);
    return (
      <>
        {/* The recent lines are the column's middle layer while they show
            (hudStack): the speed readout and Done stand above them. */}
        {recent.length > 0 && (
          <div ref={linesSlot} style={{
            position: 'fixed', ...place, bottom: `calc(${place.bottom === 16 ? '16px' : place.bottom} + ${BAR_H + 8}px)`, height: 'auto',
            zIndex: 46, display: 'flex', flexDirection: 'column', gap: 3, pointerEvents: 'none', alignItems: 'flex-start',
          }}>
            {recent.map((m, i) => (
              <div key={`${m.ts}-${i}`} style={{ background: 'rgba(10,6,3,0.66)', color: '#ffe9c4', font: '13px system-ui', padding: '4px 10px', borderRadius: 10, maxWidth: '100%' }}>
                {m.priv && <span title="Private: only your table" style={{ marginRight: 4 }}>🔒</span>}
                <b style={{ color: m.me ? '#ffd27d' : '#9dff7d' }}>{m.handle}</b> {m.text}
              </div>
            ))}
          </div>
        )}
        <div style={bar} ref={barRef}>
          <input
            ref={inputRef} value={text} maxLength={200} placeholder={placeholder || (mate ? `🔒 Private with ${mate.by}…` : 'Say something to people nearby…')}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') send(); else if (e.key === 'Escape') setMode('player'); }}
            style={{
              flex: 1, minWidth: 0, height: 40, padding: '0 14px', borderRadius: 12, outline: 'none',
              border: '1px solid rgba(255,178,94,0.3)', background: 'rgba(0,0,0,0.35)', color: '#fff', fontSize: 15,
            }}
          />
          <button type="button" onClick={send} style={{ ...icon, width: 'auto', padding: '0 14px', background: ACCENT, color: '#1c1206', font: '800 13.5px system-ui' }}>Send</button>
          <button type="button" onClick={() => setMode('player')} aria-label="Close chat" style={icon}>✕</button>
        </div>
      </>
    );
  }

  // Tap what is playing to change the station: the radio wheel (game/hud
  // RadioWheel) opens on this event, the same one its old floating button sent.
  const openStations = () => window.dispatchEvent(new CustomEvent('ht:radio-open'));
  return (
    <div style={bar} ref={barRef} role="region" aria-label="Now playing">
      <button
        type="button" onClick={openStations} title="Change station"
        style={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'center', gap: 8, background: 'none', border: 'none', padding: 0, color: 'inherit', textAlign: 'left', cursor: 'pointer' }}
      >
      {art
        ? <img src={art} alt="" style={{ flex: 'none', width: 40, height: 40, borderRadius: 10, objectFit: 'cover' }} />
        : <div aria-hidden style={{ ...icon, cursor: 'inherit', fontSize: 18 }}>♪</div>}
      {/* minWidth:0 or the titles refuse to ellipsise and push the buttons off. */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 10, opacity: 0.65, letterSpacing: 1, textTransform: 'uppercase', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', color: '#ffd27d' }}>
          {statusText || ' '}
        </div>
        <div style={{ fontSize: 13.5, fontWeight: 700, marginTop: 2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {ticker}
        </div>
      </div>
      </button>
      {/* Signed-in only: a guest has nowhere to put a like (hidden, not dead). */}
      {likeable && onLike && (
        <button
          type="button" onClick={onLike}
          aria-label={isLiked ? 'Remove from your likes' : 'Like this track'}
          style={{ ...icon, color: isLiked ? '#ff5e78' : '#ffd27d', opacity: likeBusy ? 0.5 : 1, fontSize: 19 }}
        >{isLiked ? '♥' : '♡'}</button>
      )}
      <button type="button" onClick={onToggleMusic} aria-label={musicOn ? 'Pause the station' : 'Play the station'} style={icon}>
        {musicOn ? '❚❚' : '▶'}
      </button>
      <button type="button" onClick={() => setMode('chat')} aria-label="Say something to people nearby" title="Say something to people nearby" style={icon}>💬</button>
    </div>
  );
}
